import { useCallback,useEffect,useRef,useState } from 'react';
import {isLabelPrintMessage} from '../../api/labelPrintError';
import { chineseErrorMessage, publicErrorText } from '../../api/errorMessage';
import { useRetainedRoute,useRouteActive } from './runtime';

/** Presentation policy only. API rejections and diagnostic logs remain intact. */
export function emptyStateText(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if (!text || text.length > 400) return null;
    // Never expose transport failures, infrastructure details, raw payloads or stack traces.
    if (/(服务器|网络|接口|请求|数据库|服务日志|错误编号|内部错误|重新部署|依赖|堆栈|环境配置|加载失败|读取失败|获取失败|连接失败|超时|重试|再试|系统错误|技术|Traceback|Exception|TypeError|ReferenceError|SyntaxError|fetch|network|timeout|HTTP|SQL|ECONN|ENOTFOUND|localhost|request[_ -]?id|stack|undefined|\bnull\b|\/api\/|https?:\/\/|<[^>]+>|\{\s*")/i.test(text)) return null;
    return text;
}

export function businessMessage(value: unknown): string | null {
    // These are classified business failures, not raw transport/stack messages.
    if(isLabelPrintMessage(value))return value;
    if (typeof value === 'string' && /^[\s]*[\[{<]/.test(value)) return null;
    const reason=chineseErrorMessage(value,'');
    if (reason) return publicErrorText(reason);
    // Retain existing English business guidance, but never raw diagnostics.
    const text=emptyStateText(value);
    if (text && /please |required|invalid|must |at least|at most|already |saved|copied|success|sign in|log in|password|permission|insufficient|not found|no .*available|no matching|nothing to display/i.test(text)) return text;
    return null;
}

export const NOTICE_EVENT = 'sseeaa-business-notice';
export const NOTICE_VISIBILITY_EVENT = 'sseeaa-business-notice-visibility';
export type NoticeTone = 'info' | 'error' | 'warning' | 'success';
export function notifyUser(message: unknown, tone: NoticeTone = 'info', options?:{key:string}) {
    const safe = businessMessage(message);
    if (safe) window.dispatchEvent(new CustomEvent(NOTICE_EVENT, { detail: { message: safe, tone, ...options } }));
}

export type ActionStatus = 'idle' | 'pending' | 'success';
export const ACTION_SUCCESS_MS = 900;

/** Only a fulfilled action confirms success. Loading ending alone does not. */
export function useActionFeedback(failureMessage = '') {
    const retained = useRetainedRoute();
    const active = useRouteActive();
    const canNotify = useRef(retained && active); canNotify.current = retained && active;
    const [status, setStatus] = useState<ActionStatus>('idle');
    const busy = useRef(false);
    const mounted = useRef(true);
    const reset = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; clearTimeout(reset.current); };
    }, []);
    const run = useCallback(async (action: () => unknown, confirmSuccess = false, onSuccessEnd?: () => void): Promise<boolean> => {
        if (busy.current) return false;
        busy.current = true;
        clearTimeout(reset.current);
        if (mounted.current) setStatus('idle');
        try {
            const value = action();
            const asynchronous = value != null && typeof (value as PromiseLike<unknown>).then === 'function';
            if (asynchronous && mounted.current) setStatus('pending');
            const result = asynchronous ? await value : value;
            if (result === false) { if (mounted.current) setStatus('idle'); return false; }
            if (confirmSuccess && mounted.current) {
                setStatus('success');
                reset.current = setTimeout(() => { if (mounted.current) { setStatus('idle'); onSuccessEnd?.(); } }, ACTION_SUCCESS_MS);
            }
            if (!confirmSuccess && mounted.current) setStatus('idle');
            return true;
        } catch (error) {
            if (mounted.current) setStatus('idle');
            console.error('Action failed', error);
            if (canNotify.current && mounted.current && (error as Error)?.name !== 'AbortError')
                notifyUser(chineseErrorMessage(error, failureMessage), 'error');
            return false;
        } finally { busy.current = false; }
    }, [failureMessage]);
    const dismiss = useCallback(() => {
        if (busy.current) return;
        clearTimeout(reset.current);
        if (mounted.current) setStatus('idle');
    }, []);
    return { status, run, dismiss };
}
